import React, { useState, useEffect } from 'react';
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Logo from '../../images/logo.svg';
import './style.css';

const ResetPasswordPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!token) {
            toast.error(t('resetPasswordPage.invalidToken'));
            navigate('/login');
        }
    }, [token, navigate, t]);

    // Validate password
    const validatePassword = (password) => {
        if (!password) {
            return t('resetPasswordPage.passwordRequired');
        }
        if (password.length < 8) {
            return t('resetPasswordPage.passwordMinLength');
        }
        return '';
    };

    // Validate confirm password
    const validateConfirmPassword = (confirmPassword, password) => {
        if (!confirmPassword) {
            return t('resetPasswordPage.confirmRequired');
        }
        if (confirmPassword !== password) {
            return t('resetPasswordPage.passwordMismatch');
        }
        return '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate inputs
        const passwordErr = validatePassword(password);
        const confirmPasswordErr = validateConfirmPassword(confirmPassword, password);

        setPasswordError(passwordErr);
        setConfirmPasswordError(confirmPasswordErr);

        if (passwordErr || confirmPasswordErr) {
            return;
        }

        setIsSubmitting(true);

        try {
            const apiBase = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8000');
            const response = await fetch(`${apiBase}/api/v1/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                    new_password: password
                })
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error?.message || t('resetPasswordPage.failedMessage'));
            }

            toast.success(t('resetPasswordPage.successMessage'));
            navigate('/login');
        } catch (err) {
            console.error('Reset password error:', err);
            const errorMessage = err.message || t('resetPasswordPage.failedMessage');
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        if (passwordError) {
            setPasswordError('');
        }
        if (error) {
            setError('');
        }
    };

    const handleConfirmPasswordChange = (e) => {
        setConfirmPassword(e.target.value);
        if (confirmPasswordError) {
            setConfirmPasswordError('');
        }
        if (error) {
            setError('');
        }
    };

    return (
        <div className="wpo-account-area">
            {/* Navigation Header */}
            <div className="auth-page-header">
                <Link to="/" className="back-to-home">
                    <i className="ti-arrow-left"></i>
                </Link>
                <Link to="/" className="auth-logo">
                    <img src={Logo} alt={t('verifyEmail.logoAlt')} />
                </Link>
            </div>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <form onSubmit={handleSubmit} className="wpo-account-form">
                            <Grid container spacing={3}>
                                <Grid item xs={12}>
                                    <div className="wpo-account-form-area">
                                        <div className="wpo-logo">
                                            <h2>{t('resetPasswordPage.title')}</h2>
                                            <p style={{ fontSize: '16px', color: '#666', marginTop: '10px' }}>
                                                {t('resetPasswordPage.description')}
                                            </p>
                                        </div>

                                        {/* Error Alert */}
                                        {error && (
                                            <Alert severity="error" onClose={() => setError('')} style={{ marginBottom: '20px' }}>
                                                {error}
                                            </Alert>
                                        )}

                                        <div className="wpo-account-form">
                                            <TextField
                                                fullWidth
                                                label={t('resetPasswordPage.newPassword')}
                                                variant="outlined"
                                                type="password"
                                                value={password}
                                                onChange={handlePasswordChange}
                                                error={!!passwordError}
                                                helperText={passwordError || t('resetPasswordPage.passwordHint')}
                                                disabled={isSubmitting}
                                                margin="normal"
                                            />
                                            <TextField
                                                fullWidth
                                                label={t('resetPasswordPage.confirmNewPassword')}
                                                variant="outlined"
                                                type="password"
                                                value={confirmPassword}
                                                onChange={handleConfirmPasswordChange}
                                                error={!!confirmPasswordError}
                                                helperText={confirmPasswordError}
                                                disabled={isSubmitting}
                                                margin="normal"
                                            />
                                        </div>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            type="submit"
                                            className="theme-btn"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <CircularProgress size={20} style={{ marginRight: '10px', color: 'white' }} />
                                                    {t('resetPasswordPage.resetting')}
                                                </>
                                            ) : (
                                                t('resetPasswordPage.resetButton')
                                            )}
                                        </Button>
                                        <div className="account-form-link">
                                            <p>{t('resetPasswordPage.rememberPassword')} <Link to="/login">{t('resetPasswordPage.backToLogin')}</Link></p>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ResetPasswordPage;
